import { isPlatformServer } from '@angular/common';
import { DOCUMENT, Injectable, PLATFORM_ID, inject } from '@angular/core';

import {
    DeviceService,
    LocationChangeEvent,
    NavigationService,
    OnFeatureInit,
    TRACKING_SERVICE_PROVIDER,
    TagManagerService as TagManagerCoreService,
    TimerService,
    TrackingService,
    UserEvent,
    UserLoginEvent,
    UserService,
    WINDOW,
} from '@frontend/vanilla/core';
import { first, firstValueFrom } from 'rxjs';

import { GtmService } from './gtm.service';
import { PageViewTrackingService } from './page-view-tracking.service';
import { TagManagerService } from './tag-manager.service';
import { TrackingValueGettersService } from './tracking-value-getters.service';
import { TrackingConfig } from './tracking.client-config';
import { UtmService } from './utm-service';

@Injectable()
export class TrackingBootstrapService implements OnFeatureInit {
    readonly #window = inject(WINDOW);
    private readonly _doc = inject(DOCUMENT);
    private readonly gtmService = inject(GtmService);
    private readonly isServer = isPlatformServer(inject(PLATFORM_ID));
    private readonly navigationService = inject(NavigationService);
    private readonly pageViewTrackingService = inject(PageViewTrackingService);
    private readonly user = inject(UserService);
    private readonly trackingConfig = inject(TrackingConfig);
    private readonly tagManagerService = inject(TagManagerService);
    private readonly valueGetters = inject(TrackingValueGettersService);
    private readonly utmService = inject(UtmService);
    private readonly deviceService = inject(DeviceService);
    private readonly trackingService = inject(TrackingService);
    private readonly tagManagerCoreService = inject(TagManagerCoreService);
    private readonly trackingServiceProvider = inject(TRACKING_SERVICE_PROVIDER);
    private readonly timerService = inject(TimerService);

    async onFeatureInit() {
        await firstValueFrom(this.trackingConfig.whenReady);

        if (this.isServer || !this.trackingConfig.isEnabled) {
            return;
        }

        this.gtmService.renderGTMTag();

        this.tagManagerCoreService.set(this.tagManagerService);
        this.trackingService.set(this.trackingServiceProvider);

        await this.trackingService.addInitialValues();

        // Browser data
        this.trackingServiceProvider.updateDataLayer({
            'browser.screenResolution': this.valueGetters.browserScreenResolution(),
        });

        this.deviceService.orientation.subscribe(() => {
            this.trackingServiceProvider.updateDataLayer({
                'browser.orientation': this.deviceService.currentOrientation,
            });
        });

        this.trackingServiceProvider.setReferrer(this._doc.referrer);

        this.timerService.scheduleIdleCallback(
            () => this.pageViewTrackingService.trackPageView(this.utmService.parseFromUrl(this.#window.location.href)),
            1000,
        );

        this.navigationService.locationChange.subscribe((e: LocationChangeEvent) => {
            if (e.previousUrl === e.nextUrl) {
                return;
            }

            this.trackingServiceProvider.setReferrer(e.previousUrl);
            this.pageViewTrackingService.trackPageView(this.utmService.parseFromUrl(e.nextUrl), e.id);
        });

        this.user.events.pipe(first((e: UserEvent) => e instanceof UserLoginEvent)).subscribe(() => {
            this.trackingServiceProvider.updateUserContactabilityStatus();
        });

        this.tagManagerService.init();
    }
}
